/**
 * Hook for checking user permissions
 */

import { useMemo } from 'react';
import { useEnrichedAuth } from '../../../../hooks/useEnrichedAuth';

export interface UsePermissionsReturn {
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasAllRoles: (roles: string[]) => boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  canAccess: (roles?: string[], permissions?: string[], requireAll?: boolean) => boolean;
}

/**
 * Hook for checking user permissions and roles
 *
 * @example
 * ```tsx
 * const { hasRole, hasPermission, canAccess } = usePermissions();
 *
 * if (hasRole('admin')) {
 *   // Show admin content
 * }
 *
 * if (canAccess(['admin', 'staff'], ['edit:users'])) {
 *   // Show edit button
 * }
 * ```
 */
export function usePermissions(): UsePermissionsReturn {
  // Get user from SDK via enriched auth hook
  const { user } = useEnrichedAuth();
  const userRoles = user?.roles || [];
  const userPermissions = user?.permissions || [];

  const hasRole = useMemo(
    () => (role: string) => userRoles.includes(role),
    [userRoles]
  );

  const hasAnyRole = useMemo(
    () => (roles: string[]) => roles.some((role) => userRoles.includes(role)),
    [userRoles]
  );

  const hasAllRoles = useMemo(
    () => (roles: string[]) => roles.every((role) => userRoles.includes(role)),
    [userRoles]
  );

  const hasPermission = useMemo(
    () => (permission: string) => userPermissions.includes(permission),
    [userPermissions]
  );

  const hasAnyPermission = useMemo(
    () => (permissions: string[]) =>
      permissions.some((perm) => userPermissions.includes(perm)),
    [userPermissions]
  );

  const hasAllPermissions = useMemo(
    () => (permissions: string[]) =>
      permissions.every((perm) => userPermissions.includes(perm)),
    [userPermissions]
  );

  const canAccess = useMemo(
    () =>
      (roles?: string[], permissions?: string[], requireAll: boolean = false) => {
        if (!roles && !permissions) return true;

        const roleCheck = roles
          ? requireAll
            ? hasAllRoles(roles)
            : hasAnyRole(roles)
          : true;

        const permCheck = permissions
          ? requireAll
            ? hasAllPermissions(permissions)
            : hasAnyPermission(permissions)
          : true;

        return roleCheck && permCheck;
      },
    [hasAllRoles, hasAnyRole, hasAllPermissions, hasAnyPermission]
  );

  return {
    hasRole,
    hasAnyRole,
    hasAllRoles,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccess,
  };
}
